import React, { useState, useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import HelpIcon from './HelpIcon';
import './OfficersAndPSCsInput.css';

const OfficersAndPSCsInput = ({ value = [], onChange = () => {}, helptext, company_number }) => {
    const [people, setPeople] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const prevPeople = useRef();

    // Initialize from value on first load
    useEffect(() => {
        if (!loaded) {
            if (Array.isArray(value) && value.length > 0) {
                setPeople(value);
            } else {
                // Initialize with one empty person
                setPeople([createEmptyPerson()]);
            }
            prevPeople.current = value;
            setLoaded(true);
        }
    }, [value, loaded]);

    // Update form state whenever local people array changes
    useEffect(() => {
        if (loaded && !isEqual(prevPeople.current, people)) {
            prevPeople.current = people;
            onChange(people);
        }
    }, [people, onChange, loaded]);

    // Sync external changes to local state
    useEffect(() => {
        if (loaded && !isEqual(value, prevPeople.current)) {
            prevPeople.current = value;
            setPeople(Array.isArray(value) && value.length > 0 ? value : [createEmptyPerson()]);
        }
    }, [value, loaded]);

    function createEmptyPerson() {
        return {
            id: `person_${Date.now()}_${Math.random()}`,
            type: 'officer',
            name: '',
            address: '',
            email: '',
            phone: '',
            dobMonth: '',
            dobYear: ''
        };
    }

    const addPerson = () => {
        setPeople([...people, createEmptyPerson()]);
    };

    const removePerson = (personId) => {
        if (window.confirm("Are you sure you want to remove this person? Press OK to Confirm.")) {
            setPeople(people.filter(p => p.id !== personId));
        }
    };

    const updatePersonField = (personId, field, newValue) => {
        setPeople(people.map(p =>
            p.id === personId ? { ...p, [field]: newValue } : p
        ));
    };

    const getTypeLabel = (type) => {
        const typeLabels = {
            'officer': 'Officer (Company Officer)',
            'psc': 'PSC (Person of Significant Control)',
            'shareholder': 'Shareholder',
            'both': 'Both (Officer and PSC)',
            'trio': 'Trio (Officer, PSC & Shareholder)'
        };
        return typeLabels[type] || type;
    };

    const formatPhone = (value) => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        return digits;
    };

    if (!loaded) return null;

    return (
        <fieldset className='officers-pscs-fieldset'>
            <legend className='officers-pscs-legend'>
                Engagement Resolution List
                <HelpIcon helptext={helptext} />
            </legend>

            <div className='officers-pscs-container'>
                {people.map((person, personIndex) => (
                    <div key={person.id} className='officer-psc-block'>
                        <div className='officer-psc-header'>
                            <h4>Person {personIndex + 1}</h4>
                            {people.length > 1 && (
                                <button
                                    type='button'
                                    className='officer-psc-remove-btn'
                                    onClick={() => removePerson(person.id)}
                                >
                                    Remove
                                </button>
                            )}
                        </div>

                        <div className='officer-psc-fields'>
                            <div className='officer-psc-field'>
                                <label>
                                    Type <span className='required'>*</span>
                                </label>
                                <select
                                    value={person.type || 'officer'}
                                    onChange={(e) => updatePersonField(person.id, 'type', e.target.value)}
                                    className='type-select-readonly'
                                >
                                    <option value='officer'>Officer (Company Officer)</option>
                                    <option value='psc'>PSC (Person of Significant Control)</option>
                                    <option value='shareholder'>Shareholder</option>
                                    <option value='both'>Both (Officer and PSC)</option>
                                    <option value='trio'>Trio (Officer, PSC & Shareholder)</option>
                                </select>
                            </div>

                            <div className='officer-psc-field'>
                                <label>
                                    Name <span className='required'>*</span>
                                </label>
                                <input
                                    type='text'
                                    value={person.name || ''}
                                    onChange={(e) => updatePersonField(person.id, 'name', e.target.value)}
                                    placeholder='Full name'
                                />
                            </div>

                            <div className='officer-psc-field'>
                                <label>
                                    Address
                                </label>
                                <textarea
                                    value={person.address || ''}
                                    onChange={(e) => updatePersonField(person.id, 'address', e.target.value)}
                                    placeholder='Full address'
                                    rows={3}
                                />
                            </div>

                            <div className='officer-psc-field-row'>
                                <div className='officer-psc-field officer-psc-field-half'>
                                    <label>Email Address</label>
                                    <input
                                        type='email'
                                        value={person.email || ''}
                                        onChange={(e) => updatePersonField(person.id, 'email', e.target.value)}
                                        placeholder='email@example.com'
                                    />
                                </div>

                                <div className='officer-psc-field officer-psc-field-half'>
                                    <label>Phone</label>
                                    <input
                                        type='tel'
                                        value={person.phone || ''}
                                        onChange={(e) => updatePersonField(person.id, 'phone', formatPhone(e.target.value))}
                                        placeholder='Phone number'
                                    />
                                </div>
                            </div>

                            <div className='officer-psc-field-row'>
                                <div className='officer-psc-field officer-psc-field-half'>
                                    <label>Date of Birth - Month</label>
                                    <select
                                        value={person.dobMonth || ''}
                                        onChange={(e) => updatePersonField(person.id, 'dobMonth', e.target.value)}
                                    >
                                        <option value=''>Select month</option>
                                        <option value='01'>January</option>
                                        <option value='02'>February</option>
                                        <option value='03'>March</option>
                                        <option value='04'>April</option>
                                        <option value='05'>May</option>
                                        <option value='06'>June</option>
                                        <option value='07'>July</option>
                                        <option value='08'>August</option>
                                        <option value='09'>September</option>
                                        <option value='10'>October</option>
                                        <option value='11'>November</option>
                                        <option value='12'>December</option>
                                    </select>
                                </div>

                                <div className='officer-psc-field officer-psc-field-half'>
                                    <label>Date of Birth - Year</label>
                                    <input
                                        type='number'
                                        value={person.dobYear || ''}
                                        onChange={(e) => updatePersonField(person.id, 'dobYear', e.target.value)}
                                        placeholder='YYYY'
                                        min='1900'
                                        max={new Date().getFullYear()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <button
                    type='button'
                    className='officer-psc-add-btn'
                    onClick={addPerson}
                >
                    + Add Another Person
                </button>
            </div>
        </fieldset>
    );
};

export default OfficersAndPSCsInput;
